import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";

import ProductsService from "../../services/productsService";

const FACETS = [
  { key: "categories", label: "Categories" },
  { key: "brand", label: "Brand" },
  { key: "radius", label: "Radius" },
  { key: "wall", label: "Wall" },
  { key: "diameter", label: "Diameter" },
];

export const ShopCategories = (props) => {
  const [facets, setFacets] = useState(null);
  const [selected, setSelected] = useState({
    categories: [],
    brand: [],
    radius: [],
    wall: [],
    diameter: [],
  });

  useEffect(() => {
    const fetchFilters = async () => {
      const prodService = ProductsService();
      const result = await prodService.getProductFilters();
      setFacets(result || {});
    };
    fetchFilters();
  }, []);

  // Sync from initialFilter when it lands (page-reload restoration).
  useEffect(() => {
    if (!props.initialFilter) return;
    setSelected({
      categories: props.initialFilter.categories || [],
      brand: props.initialFilter.brand || [],
      radius: props.initialFilter.radius || [],
      wall: props.initialFilter.wall || [],
      diameter: props.initialFilter.diameter || [],
    });
  }, [props.initialFilter]);

  const totalSelected = Object.values(selected).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  const toggle = (facetKey, value) => {
    setSelected((prev) => {
      const current = prev[facetKey] || [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      const updated = { ...prev, [facetKey]: next };
      if (props.updateFilter) props.updateFilter(updated);
      return updated;
    });
  };

  const clearAll = () => {
    const empty = { categories: [], brand: [], radius: [], wall: [], diameter: [] };
    setSelected(empty);
    if (props.updateFilter) props.updateFilter(empty);
  };

  const renderFacetOptions = (facetKey) => {
    if (!facets) {
      return (
        <Stack spacing={1}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="text" height={28} animation="wave" />
          ))}
        </Stack>
      );
    }
    const options = facets[facetKey] || [];
    if (options.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          No options
        </Typography>
      );
    }
    return (
      <FormGroup>
        {options.map((opt) => (
          <FormControlLabel
            key={opt}
            control={
              <Checkbox
                size="small"
                checked={(selected[facetKey] || []).includes(opt)}
                onChange={() => toggle(facetKey, opt)}
              />
            }
            label={
              <Typography variant="body2">
                {opt || <em style={{ opacity: 0.6 }}>(blank)</em>}
              </Typography>
            }
            sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: 14 } }}
          />
        ))}
      </FormGroup>
    );
  };

  return (
    <Card
      sx={{
        p: 0,
        border: (t) => `1px solid ${t.palette.divider}`,
        boxShadow: "none",
        position: "sticky",
        top: 80,
        maxHeight: "calc(100vh - 96px)",
        overflowY: "auto",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <TuneOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Filters
          </Typography>
          {totalSelected > 0 && (
            <Chip
              label={totalSelected}
              size="small"
              color="primary"
              sx={{ height: 20, fontWeight: 700 }}
            />
          )}
        </Stack>
        {totalSelected > 0 && (
          <Button
            size="small"
            onClick={clearAll}
            startIcon={<RestartAltOutlinedIcon fontSize="small" />}
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Clear
          </Button>
        )}
      </Stack>

      <Box>
        {FACETS.map((facet, idx) => (
          <Accordion
            key={facet.key}
            defaultExpanded={idx === 0}
            disableGutters
            elevation={0}
            sx={{
              "&:before": { display: "none" },
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              "&:last-of-type": { borderBottom: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreOutlinedIcon />}
              sx={{
                px: 2,
                "& .MuiAccordionSummary-content": {
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  my: 1,
                },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {facet.label}
              </Typography>
              {(selected[facet.key] || []).length > 0 && (
                <Chip
                  label={selected[facet.key].length}
                  size="small"
                  sx={{
                    height: 18,
                    fontWeight: 700,
                    bgcolor: "rgba(245, 166, 35, 0.18)",
                    color: "text.primary",
                  }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, py: 1, pt: 0 }}>
              {renderFacetOptions(facet.key)}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Card>
  );
};
